import React, { useRef, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import { modalBack } from 'location-helper';
import { withErrorBoundary } from 'react-error-boundary';

import { useLockBodyScroll } from '../hooks';

import Icon from './icon';
import RenderInBody from './render_in_body';
import ModalWindowContext from '../contexts/modal_window_context';
import ModalRoutingContext from '../contexts/modal_routing_context';
import DotsIcon from './dots_icon';
import ErrorBoundaryFallback from './error_boundary_fallback';

const ModalWindow = ({
  title,
  children,
  renderLeft,
  renderTitle,
  className,
  scrollableRef = useRef(),
  contentClassName,
  onBack,
  scrollToTop,
  showBack,
  dots,
  onDotsClick,
}) => {
  const ref = useRef();

  useLockBodyScroll(scrollableRef);

  // Scroll to top
  // Will not work when moving from a modal to other
  // Should probably work based in location
  // Very basic for now to achieve same as before in cashier actions
  useEffect(() => {
    // eslint-disable-next-line no-param-reassign
    if (scrollToTop) scrollableRef.current.scrollTop = 0;
  }, [scrollToTop]);

  const { isFirstModal } = useContext(ModalRoutingContext);

  return (
    <ModalWindowContext.Provider value={{ ref }}>
      <RenderInBody>
        <div className={`modal_wrapper${className ? ` ${className}` : ''}`} ref={ref}>
          <div className="modal_header">
            <div className="modal_header_info_wrapper">
              {(!isFirstModal || showBack) && <Icon className="back" id="arrow-left" onClick={onBack} />}
              {renderLeft && renderLeft()}
            </div>
            <div className="modal_title_wrapper">
              <span className="modal_title">
                {renderTitle ? renderTitle() : title}
              </span>
              {dots && <DotsIcon onDotsClick={onDotsClick} />}
            </div>
            <span className="close">
              <Icon id="close" big active onClick={modalBack} />
            </span>
          </div>

          <div className="modal_contents_wrapper scrollable has-scroll-bars" ref={scrollableRef}>
            <div className={`modal_contents${contentClassName ? ` ${contentClassName}` : ''}`}>
              {children}
            </div>
          </div>
        </div>
      </RenderInBody>
    </ModalWindowContext.Provider>
  );
};

ModalWindow.propTypes = {
  title: PropTypes.string,
  children: PropTypes.instanceOf(Object).isRequired,
  renderLeft: PropTypes.func,
  renderTitle: PropTypes.func,
  className: PropTypes.string,
  scrollableRef: PropTypes.instanceOf(Object),
  contentClassName: PropTypes.string,
  onBack: PropTypes.func,
  scrollToTop: PropTypes.bool,
  showBack: PropTypes.bool,
  dots: PropTypes.bool,
  onDotsClick: PropTypes.func,
};

ModalWindow.defaultProps = {
  title: '',
  renderLeft: null,
  renderTitle: null,
  className: '',
  scrollableRef: undefined,
  contentClassName: '',
  onBack: () => { window.history.back(); },
  scrollToTop: false,
  showBack: false,
  dots: false,
  onDotsClick: undefined,
};

export default withErrorBoundary(ModalWindow, {
  FallbackComponent: ErrorBoundaryFallback,
});

// showBack prop should be used very rarely, only when modals are rendered not
// as part of the normal modal routing (offers own router)
// We will probably need a prop in order to force it not to show, when a modal is not the first
// But we want the user not to go back
// Once we have everything properly routed and the prop to not go back
// probably showBack will not be needed
